<template lang="pug">
  .teamMatches
    ul.individualMatches
      IndividualMatchCard(
        v-for="card in individualMatchCards"
        :key="'individual-' + card.id"
        :card="card"
        :is-development="isDevelopment"
        @updated="getHomeMatches"
        @set-debug-phase="setDebugPhase"
      )
    ul.teamMatchList
      TeamMatchCard(
        v-for="card in teamMatchCards"
        :key="'team-' + card.id"
        :card="card"
        :is-development="isDevelopment"
        @set-debug-phase="setTeamDebugPhase"
      )
</template>
<script>
import IndividualMatchCard from '@/components/Home/IndividualMatchCard'
import TeamMatchCard from '@/components/Home/TeamMatchCard'

const PHASE = {
  BEFORE_START: 'beforeStart',
  IN_PROGRESS: 'inProgress',
  CALCULATING: 'calculating',
  RESULT: 'result',
}

const TEAM_DEBUG_PHASE = {
  NO_TEAM: 'noTeam',
  FORMING: 'forming',
  FORMED: 'formed',
  IN_PROGRESS: 'inProgress',
  CALCULATING: 'calculating',
  RESULT: 'result',
}

export default {
  components: {
    IndividualMatchCard,
    TeamMatchCard,
  },
  data() {
    return {
      individualMatches: [],
      teamMatches: [],
      debugPhasesByMatchId: {},
      teamDebugPhasesByMatchId: {},
    }
  },
  computed: {
    isDevelopment() {
      return process.env.NODE_ENV === 'development'
    },
    individualMatchCards() {
      return this.individualMatches.map(match =>
        this.buildIndividualMatchCard(match)
      )
    },
    teamMatchCards() {
      return this.teamMatches.map(match => this.buildTeamMatchCard(match))
    },
  },
  mounted() {
    this.getHomeMatches()
  },
  methods: {
    async getHomeMatches() {
      const matches = await this.serverCode('getHomeMatches', {
        teamChatModifiedDates: {},
      })
      console.log('getHomeMatches')
      console.log(matches)
      if (matches.individualMatches && matches.individualMatches.length > 0) {
        this.individualMatches = matches.individualMatches
      }
      if (matches.teamMatches && matches.teamMatches.length > 0) {
        this.teamMatches = matches.teamMatches
      }
    },
    // 開催前 / 期間中 / 集計中 / 最終結果発表
    getMatchPhase(match, now = Date.now()) {
      if (now < match.startDateTime) {
        return PHASE.BEFORE_START
      }
      if (now < match.endDateTime) {
        return PHASE.IN_PROGRESS
      }
      if (now < match.calculationEndDateTime) {
        return PHASE.CALCULATING
      }
      return PHASE.RESULT
    },
    getDisplayPhase(match) {
      if (!this.isDevelopment) {
        return this.getMatchPhase(match)
      }
      return this.debugPhasesByMatchId[match.id] || this.getMatchPhase(match)
    },
    setDebugPhase(matchId, phase) {
      if (!this.isDevelopment) {
        return
      }
      this.$set(this.debugPhasesByMatchId, matchId, phase)
    },
    getTeamMatchPhase(match, now = Date.now()) {
      if (now < match.startDateTime) {
        if (!match.teamBuildStatus) {
          return TEAM_DEBUG_PHASE.NO_TEAM
        }
        if (!match.isEstablished) {
          return TEAM_DEBUG_PHASE.FORMING
        }
        return TEAM_DEBUG_PHASE.FORMED
      }
      if (now < match.endDateTime) {
        return TEAM_DEBUG_PHASE.IN_PROGRESS
      }
      const resultStart =
        match.resultStartDateTime || match.calculationEndDateTime
      if (now < resultStart) {
        return TEAM_DEBUG_PHASE.CALCULATING
      }
      return TEAM_DEBUG_PHASE.RESULT
    },
    getDisplayTeamPhase(match) {
      if (!this.isDevelopment) {
        return this.getTeamMatchPhase(match)
      }
      return (
        this.teamDebugPhasesByMatchId[match.id] || this.getTeamMatchPhase(match)
      )
    },
    setTeamDebugPhase(matchId, phase) {
      if (!this.isDevelopment) {
        return
      }
      this.$set(this.teamDebugPhasesByMatchId, matchId, phase)
    },
    buildIndividualMatchCard(match) {
      const phase = this.getDisplayPhase(match)
      let bodyType = 'stats'
      if (phase === PHASE.BEFORE_START) {
        bodyType = match.isRegistered ? 'entered' : 'entry'
      } else if (phase === PHASE.CALCULATING) {
        bodyType = 'calculating'
      }

      const rawFactions = match.factions || []
      const factions = this.isDevelopment
        ? this.getDebugFactions(rawFactions)
        : rawFactions

      return {
        id: match.id,
        isGeneral: match.isGeneral,
        name: match.name,
        phase,
        bodyType,
        startDateTime: match.startDateTime,
        // endDateTime は開催終了日の翌日0時のため、表示は1ms前の日付にする
        displayEndDateTime: match.endDateTime - 1,
        calculationEndDateTime: match.calculationEndDateTime,
        rankText: this.formatRank(match),
        stepsText: this.formatSteps(match),
        cardImageUrl: this.getCardImageUrl(match),
        hasQuestions: !!match.hasQuestions,
        questions: match.questions || [],
        hasFaction: !!(match.hasFaction && factions.length > 0),
        factions,
        factionID: match.factionID || null,
      }
    },
    buildTeamMatchCard(match) {
      const teamPhase = this.getDisplayTeamPhase(match)
      const phase = this.toTeamHeaderPhase(teamPhase)
      const bodyType = this.toTeamBodyType(teamPhase)
      const isDebugOverride =
        this.isDevelopment && !!this.teamDebugPhasesByMatchId[match.id]
      const hideRank =
        teamPhase === TEAM_DEBUG_PHASE.CALCULATING ||
        (!isDebugOverride && !!match.needsHideRank)

      return {
        id: match.id,
        isGeneral: match.isGeneral,
        name: match.name,
        phase,
        bodyType,
        startDateTime: match.startDateTime,
        // endDateTime は開催終了日の翌日0時のため、表示は1ms前の日付にする
        displayEndDateTime: match.endDateTime - 1,
        resultStartDateTime:
          match.resultStartDateTime || match.calculationEndDateTime,
        teamName: this.getTeamName(match),
        chatBadgeCount: this.getChatBadgeCount(match),
        rankText: this.formatTeamRank(match, hideRank),
        stepsText: this.formatTeamSteps(match),
      }
    },
    toTeamHeaderPhase(teamPhase) {
      if (
        teamPhase === TEAM_DEBUG_PHASE.NO_TEAM ||
        teamPhase === TEAM_DEBUG_PHASE.FORMING ||
        teamPhase === TEAM_DEBUG_PHASE.FORMED
      ) {
        return PHASE.BEFORE_START
      }
      return teamPhase
    },
    toTeamBodyType(teamPhase) {
      if (
        teamPhase === TEAM_DEBUG_PHASE.IN_PROGRESS ||
        teamPhase === TEAM_DEBUG_PHASE.RESULT
      ) {
        return 'stats'
      }
      return teamPhase
    },
    getTeamName(match) {
      if (this.isDevelopment) {
        return match.teamName || 'ダイエットチーム'
      }
      return match.teamName || ''
    },
    getChatBadgeCount(match) {
      if (this.isDevelopment) {
        return match.chatBadgeCount > 0 ? match.chatBadgeCount : 4
      }
      return match.chatBadgeCount || 0
    },
    getCardImageUrl(match) {
      if (!match.cardImages || match.cardImages.length === 0) {
        return null
      }
      return match.cardImages[0].url || null
    },
    formatNumber(value) {
      return Number(value).toLocaleString()
    },
    getDebugRankText() {
      return '2,348 / 12,280'
    },
    getDebugStepsText() {
      return '8,320'
    },
    getDebugFactions(factions) {
      if (!factions.length) {
        return factions
      }

      const debugStats = [
        { count: 1248, averageSteps: 9520 },
        { count: 986, averageSteps: 8740 },
        { count: 1102, averageSteps: 9100 },
        { count: 834, averageSteps: 7980 },
        { count: 1056, averageSteps: 8450 },
      ]

      const withStats = factions.map((faction, index) => {
        const stats = debugStats[index % debugStats.length]
        return {
          ...faction,
          count: stats.count,
          averageSteps: stats.averageSteps,
        }
      })

      const ranked = [...withStats].sort(
        (a, b) => b.averageSteps - a.averageSteps
      )
      const rankById = {}
      ranked.forEach((faction, index) => {
        rankById[faction.id] = index + 1
      })

      return withStats.map(faction => ({
        ...faction,
        rank: rankById[faction.id],
      }))
    },
    formatRank(match) {
      if (this.isDevelopment) {
        return this.getDebugRankText()
      }
      if (match.rank == null) {
        return '-'
      }
      const rank = this.formatNumber(match.rank)
      const total =
        match.userCount != null ? this.formatNumber(match.userCount) : '-'
      return `${rank} / ${total}`
    },
    formatSteps(match) {
      if (this.isDevelopment) {
        return this.getDebugStepsText()
      }
      if (match.steps == null) {
        return '-'
      }
      return `${this.formatNumber(match.steps)}`
    },
    formatTeamRank(match, hideRank) {
      if (this.isDevelopment) {
        if (hideRank) {
          return '? / 12,280'
        }
        return this.getDebugRankText()
      }
      const total =
        match.teamCount != null ? this.formatNumber(match.teamCount) : '-'
      if (hideRank) {
        return `? / ${total}`
      }
      if (match.rank == null) {
        return '-'
      }
      return `${this.formatNumber(match.rank)} / ${total}`
    },
    formatTeamSteps(match) {
      if (this.isDevelopment) {
        return this.getDebugStepsText()
      }
      if (match.steps == null) {
        return '-'
      }
      return `${this.formatNumber(match.steps)}`
    },
  },
}
</script>
<style scoped lang="scss">
.teamMatches {
  .individualMatches,
  .teamMatchList {
    list-style: none;
    margin: 0;
    padding: 0;
  }
}
</style>
